import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Turret } from './Turret';
import { useStore } from '../../state/store';
import type { Building } from '../Scene';

/**
 * Iron Worm — segmented mechanical serpent that SWIMS through the earth like a sandworm:
 * it travels continuously toward its target while its depth follows a wave, so the head
 * breaches in a moving arc, the body humps roll across the field behind it, and it plunges
 * back under without ever stopping. Segments follow the head's actual path through space
 * (a recorded trail), so the body snakes through the head's turns and dives.
 *
 * Only the parts that have BREACHED (above the terrain) can be hit or fire mortars; the
 * head is the kill point. Each body segment is independently destroyable to whittle down
 * its mortar count.
 */
const HEAD_HP = 60;
const SEG_HP = 12;
const SEG_COUNT = 8;
/** Centre-to-centre segment spacing. The scaled body cylinders are ~31u long, so 26u spacing
 *  overlaps neighbours ~15% — an articulated vertebrae chain. (At the old 18u they buried 70%
 *  into each other and the arc pitch made the ends jut out sideways — a jumble, not a worm.) */
const SEG_SPACING = 26;
const SPEED = 30;
const SCORE = 9500;
const MORTAR_INTERVAL = 1.6; // per breached segment
/** Porpoise wave: distance for one full dive-breach-dive cycle, and how high/deep it swings.
 *  Body is ~208u, so roughly two-thirds of a wave is in the body at once — one big rolling hump. */
const WAVELENGTH = 300;
const WAVE_AMP = 42;
/** Record a head trail point every this many units of travel (the body poses along this trail). */
const PATH_STEP = 3;
const BODY_LEN = SEG_COUNT * SEG_SPACING; // arc length of the whole chain

interface Seg {
  id: string;
  group: THREE.Group;
  hp: number;
  destroyed: boolean;
  mortarTimer: number;
  isHead: boolean;
}

type Stage = 'alive' | 'dying' | 'done';

/** One recorded point of the head's trail, with cumulative arc length from the trail start. */
interface TrailPt { p: THREE.Vector3; cum: number; }

export class IronWorm extends Entity {
  stage: Stage = 'alive';
  forward = new THREE.Vector3(0, 0, -1);
  readonly speed = SPEED;
  // Head HP is tracked on the Seg.hp field for `head`; HEAD_HP is the initial value passed there.
  private segs: Seg[] = [];
  private currentTarget: { x: number; z: number } | null = null;
  private dieTimer = 0;
  private dustTimer = 0;
  /** Head trail (world space, oldest first) the body poses along. */
  private trail: TrailPt[] = [];
  /** Total ground distance the head has travelled — drives the porpoise wave. */
  private travel = 0;
  /** True last frame the head was above ground — detects breach/dive moments for FX. */
  private headWasUp = false;
  private headHpMax = HEAD_HP;

  /** Depth of the head relative to the terrain at `travel` units of journey: starts at the
   *  deepest point (fully buried) and breaches within the first quarter wave. */
  private depthAt(travel: number): number {
    return -WAVE_AMP * Math.cos((2 * Math.PI * travel) / WAVELENGTH);
  }

  constructor(spawnPos: THREE.Vector3) {
    super('tank');
    this.isBoss = true;
    this.hp = 999;
    this.radius = 24;
    this.group.position.copy(spawnPos); // spawnPos.y is the terrain surface (set by Game)

    const built = makeWormMesh();
    this.group.add(built.mesh);
    this.segs = built.segs;
    for (const p of this.segs) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts
    this.headHpMax = this.segs[0]?.hp ?? HEAD_HP;

    // Aim the journey at the map centre until a village target is picked.
    this.forward.set(-spawnPos.x, 0, -spawnPos.z).normalize();
    // Seed the trail straight back from the spawn point, fully buried, so the body has a
    // path to pose along from frame one and the worm "swims up" as it advances.
    const seedFrom = -(BODY_LEN + 40);
    for (let s = seedFrom; s <= 0; s += PATH_STEP) {
      const x = spawnPos.x + this.forward.x * s;
      const z = spawnPos.z + this.forward.z * s;
      this.trail.push({ p: new THREE.Vector3(x, spawnPos.y - WAVE_AMP, z), cum: s - seedFrom });
    }

    // Hit parts: head + each segment. The box tiles the FULL inter-segment spacing in z
    // (body ±6 local ×2.2 ≈ ±13 world ≈ half of SEG_SPACING) so consecutive boxes abut — shots
    // anywhere along the now-continuous hull register instead of slipping through the ~13u gaps
    // that used to sit between segment centers. x/y track the ~7.9u body radius.
    this.hitParts = [
      ...this.segs.map((s) => ({
        id: s.id,
        box: new THREE.Box3(
          new THREE.Vector3(-3.4, -3.4, s.isHead ? -4 : -6),
          new THREE.Vector3(3.4, 3.4, 6),
        ),
        object: s.group,
      })),
    ];

    // Head AA gun — defends the worm by firing on the player while the head has breached.
    const head = this.segs.find((s) => s.isHead);
    if (head) {
      this.headTurret = new Turret({ range: 850, interval: 0.55, speed: 280, size: 1.4 });
      this.headTurret.attachTo(head.group, 0, 2.6, 0);
    }

    // Pose the body along the seeded trail immediately (buried) so nothing flashes at origin.
    this.poseBody();
  }

  private headTurret?: Turret;

  alive(): boolean { return this.stage === 'alive'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'alive') return;
    const seg = this.segs.find((s) => s.id === partId);
    if (!seg || seg.destroyed) return;
    // Only BREACHED coils can be hurt — a part still under the earth shrugs the shot off.
    {
      const wp = new THREE.Vector3();
      seg.group.getWorldPosition(wp);
      if (wp.y < ctx.game.gameScene.heightAt(wp.x, wp.z) + 3) {
        ctx.game.shieldedFeedback(ctx, wp.setY(ctx.game.gameScene.heightAt(wp.x, wp.z) + 4),
          'BURROWED — STRIKE THE COILS THAT BREACH');
        return;
      }
    }
    seg.hp -= amount;
    const wp = new THREE.Vector3();
    seg.group.getWorldPosition(wp);
    new Explosion(wp, 4, 0.22, 'fire').addTo(ctx);
    if (seg.hp <= 0) {
      seg.destroyed = true;
      new Explosion(wp, 22, 0.7, 'fire').addTo(ctx);
      new Explosion(wp, 16, 3, 'smoke').setDrift(new THREE.Vector3(0, 9, 0)).addTo(ctx);
      ctx.game.audio.playBoom(0.55);
      seg.group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          const mat = m.material as THREE.MeshStandardMaterial;
          mat.color?.setHex(0x141210);
        }
      });
      // If the head segment dies → boss is dead.
      if (seg.isHead || partId === 'head') this.beginDying(ctx);
    }
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'dying') { this.updateDying(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Pick a target village; retarget when we've effectively arrived (the worm never stops).
    if (!this.currentTarget) {
      const v = pickVillage(this.group.position, ctx);
      if (v) this.currentTarget = { x: v.position.x, z: v.position.z };
    }
    if (this.currentTarget) {
      const dx = this.currentTarget.x - this.group.position.x;
      const dz = this.currentTarget.z - this.group.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 80) this.currentTarget = null;
      else this.forward.lerp(new THREE.Vector3(dx / dist, 0, dz / dist), 0.04).normalize();
    }

    // SWIM: the head advances every frame; its depth rides the porpoise wave, so it breaches,
    // arcs forward through the air, and plunges back under — all while travelling.
    // ENRAGE at half head HP: it thrashes — faster swim, faster mortars.
    const enraged = (this.segs[0]?.hp ?? 0) <= this.headHpMax / 2;
    const spd = SPEED * (enraged ? 1.3 : 1);
    this.travel += spd * dt;
    const hx = this.group.position.x + this.forward.x * spd * dt;
    const hz = this.group.position.z + this.forward.z * spd * dt;
    const ground = ctx.game.gameScene.heightAt(hx, hz);
    this.group.position.set(hx, ground, hz); // entity anchor stays at terrain level (radar/targeting)
    const headY = ground + this.depthAt(this.travel);

    // Record the trail (the body poses along it) and trim what the tail no longer needs.
    const last = this.trail[this.trail.length - 1];
    const head = new THREE.Vector3(hx, headY, hz);
    if (!last || last.p.distanceTo(head) >= PATH_STEP) {
      this.trail.push({ p: head, cum: (last ? last.cum + last.p.distanceTo(head) : 0) });
      const headCum = this.trail[this.trail.length - 1].cum;
      while (this.trail.length > 2 && this.trail[1].cum < headCum - (BODY_LEN + 60)) this.trail.shift();
    }
    this.poseBody();

    // Breach / dive FX at the head — eruption of earth on the way out, splash on the way in.
    const headUp = headY > ground + 2;
    if (headUp !== this.headWasUp) {
      const at = new THREE.Vector3(hx, ground + 2, hz);
      new Explosion(at.clone(), headUp ? 34 : 24, 0.6, 'smoke').setDrift(new THREE.Vector3(0, 10, 0)).addTo(ctx);
      if (headUp) { new Explosion(at, 20, 0.4, 'fire').addTo(ctx); ctx.game.audio.playBoom(0.7); }
      this.headWasUp = headUp;
    }

    // Per-segment: breached coils fire mortars; coils crossing the surface kick up dust.
    this.dustTimer += dt;
    const doDust = this.dustTimer >= 0.16;
    if (doDust) this.dustTimer = 0;
    const wp = new THREE.Vector3();
    for (const s of this.segs) {
      if (s.destroyed) continue;
      s.group.getWorldPosition(wp);
      const g = ctx.game.gameScene.heightAt(wp.x, wp.z);
      const above = wp.y > g + 2;
      if (above) {
        s.mortarTimer -= dt;
        if (s.mortarTimer <= 0) {
          s.mortarTimer = MORTAR_INTERVAL * (enraged ? 0.7 : 1) + Math.random() * 1.2;
          this.fireMortar(ctx, s);
        }
      }
      // Dust where the body churns the surface (segment near ground level).
      if (doDust && Math.abs(wp.y - g) < 8 && Math.random() < 0.6) {
        new Explosion(new THREE.Vector3(wp.x, g + 1.5, wp.z), 8, 1.2, 'smoke')
          .setDrift(new THREE.Vector3(0, 5, 0)).addTo(ctx);
      }
    }

    // Head gun tracks + fires on the player while the head has breached.
    if (this.headTurret) this.headTurret.update(ctx, this.id, headUp);
  }

  /** Pose every segment along the recorded head trail: segment i sits i*SEG_SPACING of arc
   *  length behind the head, oriented along the local trail tangent. */
  private poseBody() {
    poseWormAlongTrail(this.segs, this.trail, this.group.position);
  }

  private fireMortar(ctx: EntityCtx, s: Seg) {
    // Pick a random live building from the nearest village; instantly destroy it.
    const v = pickVillage(this.group.position, ctx);
    if (!v) return;
    const livePool: Building[] = v.buildings.filter((b) => !b.destroyed);
    if (livePool.length === 0) return;
    const victim = livePool[Math.floor(Math.random() * livePool.length)];
    const muzzle = new THREE.Vector3();
    s.group.getWorldPosition(muzzle);
    new Explosion(muzzle, 6, 0.18, 'fire').addTo(ctx);
    new Explosion(muzzle, 5, 0.6, 'smoke').setDrift(new THREE.Vector3(0, 4, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.45);
    new Explosion(victim.position.clone(), 22, 0.8, 'fire').addTo(ctx);
    new Explosion(victim.position.clone(), 16, 3, 'smoke').setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
    victim.destroy(ctx.game.gameScene.root);
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'dying';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    // Head shatter
    const wp = new THREE.Vector3();
    this.segs[0].group.getWorldPosition(wp);
    new Explosion(wp, 60, 1.0, 'fire').addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    const t = this.dieTimer;
    // Cascade: each segment along the chain explodes in sequence.
    const segIdx = Math.floor(t / 0.18);
    if (segIdx < this.segs.length) {
      const s = this.segs[segIdx];
      if (!s.destroyed && !s.group.userData.detonated) {
        s.group.userData.detonated = true;
        const wp = new THREE.Vector3();
        s.group.getWorldPosition(wp);
        new Explosion(wp, 36, 0.9, 'fire').addTo(ctx);
        new Explosion(wp, 24, 4, 'smoke').setDrift(new THREE.Vector3(0, 12, 0)).addTo(ctx);
        if (segIdx % 2 === 0) ctx.game.audio.playBoom(0.7);
      }
    }
    // After all segments have detonated, ground-impact explosion
    if (t > this.segs.length * 0.18 + 0.4 && !this.group.userData.finalBoom) {
      this.group.userData.finalBoom = true;
      const center = this.group.position.clone();
      center.y = -460;
      new Explosion(center, 200, 1.2, 'fire').addTo(ctx);
      new Explosion(center, 140, 0.8, 'fire').addTo(ctx);
      ctx.game.audio.playBoom(1.5);
    }
    if (t > 5) this.stage = 'done';
  }
}

function pickVillage(from: THREE.Vector3, ctx: EntityCtx) {
  let best = null;
  let bestD = Infinity;
  for (const v of ctx.game.gameScene.villages) {
    if (v.destroyed) continue;
    const dx = v.position.x - from.x;
    const dz = v.position.z - from.z;
    const d = Math.hypot(dx, dz);
    if (d < bestD) { bestD = d; best = v; }
  }
  return best;
}

/* ---------------------- mesh ---------------------- */

interface WBuilt { mesh: THREE.Group; segs: Seg[]; }

const _tangent = new THREE.Vector3();
const _zAxis = new THREE.Vector3(0, 0, 1);
/** Pose segment i at i*SEG_SPACING of arc length behind the head along the recorded trail,
 *  aligned with the local trail tangent. `origin` converts trail world points into the
 *  entity group's local frame (the specimen passes (0,0,0)). Shared by the live entity and
 *  the diag specimen so the posing math can't drift between them. */
function poseWormAlongTrail(segs: Pick<Seg, 'group' | 'destroyed'>[], trail: TrailPt[], origin: THREE.Vector3) {
  if (trail.length < 2) return;
  const headCum = trail[trail.length - 1].cum;
  let j = trail.length - 1;
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const target = headCum - i * SEG_SPACING; // decreases with i → j only ever walks backward
    while (j > 1 && trail[j - 1].cum > target) j--;
    const a = trail[j - 1], b = trail[j];
    const t = THREE.MathUtils.clamp((target - a.cum) / Math.max(1e-6, b.cum - a.cum), 0, 1);
    if (s.destroyed) continue;
    s.group.position.lerpVectors(a.p, b.p, t).sub(origin);
    // Head cone points local -Z: align -Z with the direction of travel (+tangent).
    _tangent.subVectors(b.p, a.p).normalize().multiplyScalar(-1);
    s.group.quaternion.setFromUnitVectors(_zAxis, _tangent);
  }
}

/** Diag-only: the worm posed mid-porpoise on a synthetic trail (crest rolling through the
 *  body, head plunging), using the live wave constants + poser. */
export function makeWormSpecimen(): THREE.Group {
  const built = makeWormMesh();
  const trail: TrailPt[] = [];
  const headTravel = 0.75 * WAVELENGTH; // head on the way back down; crest sits mid-body
  const from = headTravel - (BODY_LEN + 40);
  for (let s = from; s <= headTravel; s += PATH_STEP) {
    const y = -WAVE_AMP * Math.cos((2 * Math.PI * s) / WAVELENGTH);
    trail.push({ p: new THREE.Vector3(0, y, s - from), cum: s - from });
  }
  poseWormAlongTrail(built.segs, trail, new THREE.Vector3());
  return built.mesh;
}

function makeWormMesh(): WBuilt {
  const g = new THREE.Group();
  const segs: Seg[] = [];
  const armor = new THREE.MeshStandardMaterial({ color: 0x3a352f, roughness: 0.85, flatShading: true });
  const armorDark = new THREE.MeshStandardMaterial({ color: 0x1c1814, roughness: 0.95, flatShading: true });
  const brass = new THREE.MeshStandardMaterial({ color: 0xa57a30, roughness: 0.5, metalness: 0.8 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.45, metalness: 0.7 });
  const eye = new THREE.MeshBasicMaterial({ color: 0xff6030 });

  for (let i = 0; i < SEG_COUNT; i++) {
    const isHead = i === 0;
    const segGroup = new THREE.Group();
    if (isHead) {
      // Armored head: drill cone + eyes + maw
      const cone = new THREE.Mesh(new THREE.ConeGeometry(4.5, 7, 16), armor);
      cone.rotation.x = -Math.PI / 2;
      cone.position.set(0, 0, -2);
      segGroup.add(cone);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(4.5, 24, 16), armorDark);
      cap.position.set(0, 0, 1.5);
      cap.scale.set(1, 1, 0.7);
      segGroup.add(cap);
      // Brass armor bands
      // Armor bands wrap AROUND the head axis (Z) — torus default, no rotation.
      for (let b = 0; b < 3; b++) {
        const band = new THREE.Mesh(new THREE.TorusGeometry(4.6 - b * 0.4, 0.25, 8, 24), brass);
        band.position.set(0, 0, -1 - b * 1.5);
        segGroup.add(band);
      }
      // Neck — tapered collar running back from the skull toward the first body segment, so the
      // head is continuous with the train instead of a floating drum.
      const neck = new THREE.Mesh(new THREE.CylinderGeometry(3.1, 3.5, 6.5, 16, 1), armor);
      neck.rotation.x = Math.PI / 2;
      neck.position.set(0, 0, 6.2);
      segGroup.add(neck);
      // Eyes
      for (const sx of [-2, 2]) {
        const e = new THREE.Mesh(new THREE.SphereGeometry(0.55, 12, 8), eye);
        e.position.set(sx, 1.2, -3.2);
        segGroup.add(e);
      }
      // Mandibles — three jaw blades hugging the drill cone, hooking FORWARD (not splayed
      // radially — that read as a sawblade disc from the side).
      for (let m = 0; m < 3; m++) {
        const a = (m / 3) * Math.PI * 2 + Math.PI / 6;
        const blade = new THREE.Mesh(new THREE.ConeGeometry(0.55, 3.6, 8), ironDark);
        blade.position.set(Math.cos(a) * 2.4, Math.sin(a) * 2.4, -5.2);
        blade.rotation.x = -Math.PI / 2 - 0.12;
        blade.rotation.z = -a + Math.PI / 2;
        segGroup.add(blade);
      }
      // Bulk: the head must dominate the body train (and the old head read as a pellet).
      segGroup.scale.setScalar(2.4);
    } else {
      // Body segments: armored CAPSULE (rounded ends), NOT a flat-ended cylinder. Two straight
      // cylinders meeting at the arc's bend showed their disc rims as ring-seams + V-notches on the
      // outer edge, so the worm read as a stack of pipe sections. Rounded caps from neighbouring
      // segments interpenetrate smoothly at any bend angle → one continuous hull. Cylinder length
      // 11 + 2×3.6 caps = 18.2 local ×2.2 ≈ 40u total; centres are 26u apart (SEG_SPACING), so each
      // capsule's caps reach ~7u past the midpoint into BOTH neighbours — constant-radius double
      // coverage with no gaps even at the crest.
      const body = new THREE.Mesh(new THREE.CapsuleGeometry(3.6, 11, 6, 18), armor);
      body.rotation.x = Math.PI / 2;
      segGroup.add(body);
      // Mortar barrel
      const mortar = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 3.0, 14), ironDark);
      mortar.rotation.x = -0.6;
      mortar.position.set(0, 3.2, 0);
      segGroup.add(mortar);
      // Brass band trim — torus hole axis is Z by default, which IS the body axis, so the rings
      // wrap AROUND the hull. (The old rotation.y=PI/2 turned them into crosswise discs that made
      // every segment read as a sliced pipe section.)
      for (let b = -1; b <= 1; b += 1) {
        const band = new THREE.Mesh(new THREE.TorusGeometry(3.78, 0.22, 8, 22), brass);
        band.position.set(0, 0, b * 4);
        segGroup.add(band);
      }
      // Bulk: at 2.2x the 14u-long segments overlap the 18u spacing, so the train reads as one
      // continuous armored serpent instead of a chain of floating pills. Hit boxes are in this
      // group's local space, so the OBB tests scale with it.
      segGroup.scale.setScalar(2.2);
    }
    g.add(segGroup);
    segs.push({
      id: isHead ? 'head' : `seg${i}`,
      group: segGroup,
      hp: isHead ? HEAD_HP : SEG_HP,
      destroyed: false,
      mortarTimer: 0.3 + i * 0.2,
      isHead,
    });
  }

  return { mesh: g, segs };
}
